import { Component, OnInit, Input, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder } from '@angular/forms';
import { AgentErrorCode, FileManagerService } from 'xsmodules/remote-control/services/file-manager.service';
import { Subscription } from 'rxjs';
import { RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';
import { FileData } from 'xsmodules/remote-control/file-manager/fileData';
import { hd } from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { RcContext } from 'xsmodules/remote-control-ng/context/rcContext';

@Component({
  selector: 'app-file-manager-delete-item',
  templateUrl: './file-manager-delete-item.component.html',
  styleUrls: ['./file-manager-delete-item.component.scss'],
})
export class FileManagerDeleteItemComponent implements OnInit, OnDestroy {
  public errorMessage = '';
  public serverErrorMessage: string;
  private sessionEndedSubscription: Subscription;
  private selectedItem: FileData;
  public itemType: string;

  @Input() dialogParams: hd.PromptDialogParams;
  @Input() rcState: RemoteControlDeviceState;

  constructor(
    private fileManagerService: FileManagerService,
    private modalDialog: MatDialogRef<FileManagerDeleteItemComponent>,
    private saI18nPipe: XsI18nPipe,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.loadForm();
    this.sessionEndedSubscription = this.fileManagerService.sessionEnded.subscribe(() => {
      this.onCancel();
    });
  }

  ngOnDestroy() {
    if (this.sessionEndedSubscription) {
      this.sessionEndedSubscription.unsubscribe();
    }
  }

  public loadForm() {
    this.selectedItem = this.fileManagerService.getCurrentItem() as FileData;
    this.itemType = this.fileManagerService.getCurrentItemType() as string;
  }

  /** Return the error message if exists */
  public getErrorMessage = (): string => this.errorMessage || '';

  public getServerErrorMessage = (): string => this.serverErrorMessage || '';

  /** Cancels the modal dialog */
  public onCancel = (): void => {
    this.modalDialog.close('cancel');
  };

  public getDescriptionMessage = (): string =>
    this.itemType === 'file'
      ? this.saI18nPipe.transform('fileDeleteItem_dialog_Description_File', this.selectedItem.fileName)
      : this.saI18nPipe.transform('fileDeleteItem_dialog_Description_Folder', this.selectedItem.fileName);

  public getFolderName = (): string => this.selectedItem.fileName;

  public onDelete = (): void => {
    if (!this.errorMessage) {
      // Each platforms use different shell commands to remove file/directory
      // The client should have the logic to request remove an empty or full directory

      if (this.itemType === 'file') {
        this.fileManagerService.deleteFile(this.selectedItem.fullPath).subscribe(result => {
          switch (result) {
            case AgentErrorCode.Success:
              console.log('File Delete complete' + this.selectedItem.fullPath);
              break;

            case AgentErrorCode.Access_Denied:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Access_Denied');
              break;

            case AgentErrorCode.Not_Exists:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Not_exists');
              break;

            case AgentErrorCode.Already_Exists:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Already_Exists');
              break;

            default:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Undefined', result);
          }

          if (this.errorMessage) {
            console.log(
              'File Delete ' +
                this.selectedItem.fullPath +
                ' failed. ErrorCode:' +
                result +
                ', ErrorDescription: ' +
                this.errorMessage
            );
          }

          if (!result) {
            this.modalDialog.close();
            this.searchAgain();
          }
        });
      } else if (this.itemType === 'folder') {
        this.fileManagerService.deleteFolder(this.selectedItem.fullPath).subscribe(result => {
          switch (result) {
            case AgentErrorCode.Success:
              console.log('Folder Delete complete' + this.selectedItem.fullPath);
              break;

            case AgentErrorCode.Access_Denied:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Access_Denied');
              break;

            case AgentErrorCode.Not_Exists:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Not_exists');
              break;

            case AgentErrorCode.Already_Exists:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Already_Exists');
              break;

            case AgentErrorCode.Folder_Not_Empty:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Folder_Not_Empty');
              break;

            case AgentErrorCode.Not_Supported:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Not_Supported');
              break;

            default:
              this.errorMessage = this.saI18nPipe.transform('fileDeleteItem_dialog_error_message_Undefined', result);
          }

          if (this.errorMessage) {
            console.log(
              'Folder Delete ' +
                this.selectedItem.fullPath +
                ' failed. ErrorCode:' +
                result +
                ', ErrorDescription: ' +
                this.errorMessage
            );
          }

          if (!result) {
            this.modalDialog.close();
            this.searchAgain();
          }
        });
      }
    }
  };

  /** search again after deleting the file */
  private searchAgain = () => {
    const state = this.fileManagerService.getState(RcContext.instance.sotiAssistDeviceId);
    if (state) {
      state.clearPreviousSearchResults();
      state.onSearchButton();
      state.selectedTreeNodes = [];
      state.onUpdate();
    }
  };
}
